/**
 * Enhanced API Client with JWT Token Management and Automatic Refresh
 * 
 * Features:
 * - Automatic addition of Authorization header with Bearer token
 * - Token refresh on 401/403 errors for specific endpoints
 * - Request queuing during token refresh
 * - Support for both wrapped and unwrapped API responses
 */

import axios from 'axios';
import tokenManager from './tokenManager';
import API_URL from './apiConfig'; 

const SESSION_EXPIRED_EVENT = 'auth:session-expired';
const API_KEY =  process.env.REACT_APP_AWS_API_KEY;
const TOKEN_REFRESH_STATUS_CODES = [401, 403];

const notifySessionExpired = (reason = 'session_expired') => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(SESSION_EXPIRED_EVENT, {
        detail: { reason },
      })
    );
  }
};

const hadAuthenticatedContext = (requestConfig) => {
  const inMemoryToken = tokenManager.getAuthToken();
  const inMemoryUserId = tokenManager.getUserId();
  const authHeader = requestConfig?.headers?.Authorization || requestConfig?.headers?.authorization;

  return Boolean(inMemoryToken || inMemoryUserId || authHeader);
};

// Refresh token endpoint (full URL as per guide)
const REFRESH_TOKEN_URL = `${API_URL}/auth/tokens/refresh`;

// Flag to track if a token refresh is in progress
let isRefreshing = false;

// Queue to hold requests while token is being refreshed
let failedQueue = [];

/**
 * Process queued requests after token refresh
 * @param {Error|null} error - Error if refresh failed
 * @param {string|null} token - New token if refresh succeeded
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * Refresh the access token using the refresh token cookie.
 *
 * Both apiClient.js and axiosConfig.js keep their own isRefreshing/failedQueue
 * locks (they queue different requests), but they share this single function
 * to perform the actual network call. Without deduping here, a 401 arriving
 * on one axios instance and a 401 arriving on the other at nearly the same
 * time each think no refresh is in flight and both POST /auth/tokens/refresh
 * concurrently, which gets the second call rate-limited (429) and cascades
 * into further failures. Caching the in-flight promise makes every concurrent
 * caller, regardless of which instance triggered it, await the same request.
 */
let refreshPromise = null;

export const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = performTokenRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const performTokenRefresh = async () => {
  try {
    const response = await axios.post(
      REFRESH_TOKEN_URL,
      {}, // Empty body
      {
        withCredentials: true, // Include refreshToken cookie
        headers: {
          'x-api-key': API_KEY,
        }
      }
    );

    // Handle multiple possible response structures
    const responseData = response.data?.data || response.data;
    const newToken = responseData?.accessToken || 
                     response.data?.accessToken || 
                     responseData?.token || 
                     response.data?.token;

    if (newToken) {
      tokenManager.setAuthToken(newToken);
      
      // Extract userId with fallback logic
      const userId = responseData?.id || 
                     response.data?.id || 
                     responseData?.userId || 
                     responseData?.uid ||
                     responseData?.user?.id;

      if (userId) {
        tokenManager.setUserId(userId.toString());
      } else {
        // Fallback: fetch userId from /auth/me endpoint
        try {
          const meResponse = await axios.get(`${API_URL}/user/me`, {
            headers: {
              Authorization: `Bearer ${newToken}`,
              'x-api-key': API_KEY,
            }
          });
          const meUserId = meResponse.data?.data?.id ||
                           meResponse.data?.data?.userId ||
                           meResponse.data?.id ||
                           meResponse.data?.userId;
          if (meUserId) {
            tokenManager.setUserId(meUserId.toString());
          }
        } catch (meError) {
          console.warn('⚠️ Could not fetch user ID from /auth/me');
        }
      }

      // Extract user role if available
      const userRole = responseData?.role || 
                       response.data?.role || 
                       responseData?.userRole;
      if (userRole) {
        tokenManager.setUserRole(userRole);
      }

      return newToken;
    }

    console.error('No token in refresh response');
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error.message);
    tokenManager.clear();
    return null;
  }
};

/**
 * Create axios instance with base configuration
 */
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 120000, // 120 seconds for long operations
  withCredentials: true, // Enable sending cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Endpoints that should trigger automatic token refresh on expired-token errors
 */
const refreshableEndpoints = [
  '/user/me',
  '/pet/profile',
  '/pet/profile/me',
  '/pet/transfer',
  '/pet/medical',
  '/pet/recovery',
  '/commerce',
  '/logistics',
  '/notifications',
];

/**
 * Check if endpoint should trigger token refresh
 * @param {string} url - Request URL
 * @returns {boolean} True if endpoint is refreshable
 */
const isRefreshableEndpoint = (url) => {
  if (!url) return false;
  return refreshableEndpoints.some(endpoint => url.includes(endpoint));
};

const isTokenRefreshStatus = (status) => TOKEN_REFRESH_STATUS_CODES.includes(status);

const shouldAttemptTokenRefresh = (error, originalRequest, hadAuthContext) => {
  if (!originalRequest || originalRequest._retry || !hadAuthContext) {
    return false;
  }

  if (!isRefreshableEndpoint(originalRequest.url)) {
    return false;
  }

  if (isTokenRefreshStatus(error.response?.status)) {
    return true;
  }

  // API Gateway/Lambda 403s can be hidden from Axios as CORS/network errors
  // when the error response misses CORS headers. For authenticated DDD routes,
  // try one refresh before surfacing the failure.
  return Boolean(error.request && !error.response);
};

/**
 * Request Interceptor
 * Automatically adds Authorization header and handles FormData
 */
apiClient.interceptors.request.use(
  (config) => {
    // Always add x-api-key header for DDD API compatibility
    config.headers = config.headers || {};
    config.headers['x-api-key'] = API_KEY;
    
    const token = tokenManager.getAuthToken();
    
    // Add Authorization header if token exists
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Handle FormData - let axios set the correct multipart boundary
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles token refresh on expired-token errors and error formatting
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const shouldShowSessionExpiredPopup = hadAuthenticatedContext(originalRequest);

    if (shouldAttemptTokenRefresh(error, originalRequest, shouldShowSessionExpiredPopup)) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
              return apiClient(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          
          if (newToken) {
            // Update the failed request with new token
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
            
            // Process queued requests
            processQueue(null, newToken);
            
            // Retry the original request
            return apiClient(originalRequest);
          } else {
            // Refresh failed, clear tokens
            processQueue(new Error('Token refresh failed'), null);
            tokenManager.clear();
            if (shouldShowSessionExpiredPopup) {
              notifySessionExpired('refresh_failed');
            }
            
            // Optionally redirect to login
            // window.location.href = '/login';
            
            return Promise.reject(error);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          tokenManager.clear();
          if (shouldShowSessionExpiredPopup) {
            notifySessionExpired('refresh_exception');
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
    } else if (
      error.response?.status === 401 &&
      originalRequest &&
      shouldShowSessionExpiredPopup &&
      !isRefreshableEndpoint(originalRequest.url)
    ) {
        // For non-refreshable endpoints, just clear tokens
        tokenManager.clear();
        if (shouldShowSessionExpiredPopup) {
          notifySessionExpired('unauthorized');
        }
    }

    // Handle other errors
    if (error.response) {
      if (error.response.status === 401) {
        console.log('Please login again');
      } else {
        const errorDetails = {
          status: error.response.status,
          url: error.config?.url || 'unknown',
          method: error.config?.method?.toUpperCase() || 'unknown',
          data: error.response.data
        };

        console.error(`API Error ${error.response.status}:`, errorDetails);
      }
    } else if (error.request) {
      console.error('Network Error:', error.message);
    } else {
      console.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to check if response is wrapped
 * @param {any} data - Response data
 * @returns {boolean} True if response is wrapped
 */
function isApiResponse(data) {
  return data && typeof data === 'object' && 'data' in data && 'success' in data;
}

/**
 * Helper function to unwrap API response
 * @param {any} responseData - Response data
 * @returns {any} Unwrapped data
 */
function unwrapResponse(responseData) {
  return isApiResponse(responseData) ? responseData.data : responseData;
}

/**
 * GET request helper
 * @param {string} url - Request URL
 * @param {object} config - Axios config
 * @returns {Promise<any>} Response data
 */
export const get = async (url, config = {}) => {
  const response = await apiClient.get(url, config);
  return unwrapResponse(response.data);
};

/**
 * POST request helper
 * @param {string} url - Request URL
 * @param {any} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise<any>} Response data
 */
export const post = async (url, data, config = {}) => {
  const response = await apiClient.post(url, data, config);
  return unwrapResponse(response.data);
};

/**
 * PUT request helper
 * @param {string} url - Request URL
 * @param {any} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise<any>} Response data
 */
export const put = async (url, data, config = {}) => {
  const response = await apiClient.put(url, data, config);
  return unwrapResponse(response.data);
};

/**
 * DELETE request helper
 * @param {string} url - Request URL
 * @param {object} config - Axios config
 * @returns {Promise<any>} Response data
 */
export const del = async (url, config = {}) => {
  const response = await apiClient.delete(url, config);
  return unwrapResponse(response.data);
};

/**
 * GET request with status code
 * @param {string} url - Request URL
 * @param {object} config - Axios config
 * @returns {Promise<{data: any, statusCode: number, statusText: string}>}
 */
export const getWithStatus = async (url, config = {}) => {
  const response = await apiClient.get(url, config);
  return {
    data: unwrapResponse(response.data),
    statusCode: response.status,
    statusText: response.statusText
  };
};

/**
 * POST request with status code
 * @param {string} url - Request URL
 * @param {any} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise<{data: any, statusCode: number, statusText: string}>}
 */
export const postWithStatus = async (url, data, config = {}) => {
  const response = await apiClient.post(url, data, config);
  return {
    data: unwrapResponse(response.data),
    statusCode: response.status,
    statusText: response.statusText
  };
};

/**
 * PUT request with status code
 * @param {string} url - Request URL
 * @param {any} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise<{data: any, statusCode: number, statusText: string}>}
 */
export const putWithStatus = async (url, data, config = {}) => {
  const response = await apiClient.put(url, data, config);
  return {
    data: unwrapResponse(response.data),
    statusCode: response.status,
    statusText: response.statusText
  };
};

/**
 * DELETE request with status code
 * @param {string} url - Request URL
 * @param {object} config - Axios config
 * @returns {Promise<{data: any, statusCode: number, statusText: string}>}
 */
export const deleteWithStatus = async (url, config = {}) => {
  const response = await apiClient.delete(url, config);
  return {
    data: unwrapResponse(response.data),
    statusCode: response.status,
    statusText: response.statusText
  };
};

export default apiClient;
